import { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, ChevronRight, Banknote, CornerDownLeft } from 'lucide-react';
import { money } from '../api';
import { colorDiferencia, nivelDiferencia, TOLERANCIA_MENOR, DENOMINACIONES, Denominacion } from '../../../utils/caja';

// Denominaciones de PESOS (billetes y monedas).
//
// 🚨 Los DÓLARES se quitaron el 18-sep-2026. Eran herencia de la caja dental de Neri y en un
// estudio de baile nadie cobra en dólares: obligaban a teclear 6 campos más y un tipo de cambio
// para abrir la caja. Si algún día hacen falta, vuelven por config, no por defecto.
// Los renglones salen de utils/caja.ts (DENOMINACIONES): desde el 1-oct-2026 el $20 va en billete y en moneda.

/** Mínimo de caracteres del motivo cuando el cierre no cuadra (mismo número en el backend). */
export const MOTIVO_MIN = 15;

// 📝 BORRADOR del conteo (5-oct-2026, audio de Mabel): se le cerraba el modal y perdía billetes y
// monedas ya contados; contó «como 10 veces». Ahora lo tecleado se guarda en el navegador por
// caja + acción y vuelve al reabrir. NO acredita nada: el dinero cuenta hasta darle al botón.
// Lo borra el panel al guardar con éxito (borrarBorradorCaja); caduca a las 12 h.
const BORRADOR_TTL_MS = 12 * 3600 * 1000;
type Borrador = { mxn: Record<string, string>; usd: string; vacio: boolean; motivo: string; t: number };
function leerBorrador(key?: string): Borrador | null {
  if (!key) return null;
  try {
    const b = JSON.parse(localStorage.getItem(key) || 'null') as Borrador | null;
    if (!b || Date.now() - (b.t || 0) > BORRADOR_TTL_MS) { localStorage.removeItem(key); return null; }
    return b;
  } catch { return null; }
}
export function borrarBorradorCaja(key?: string) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch { /* navegador sin almacenamiento */ }
}

const inputCls = 'w-20 rounded-lg border border-line bg-surface px-2 py-1 text-right text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';

const diffColor = colorDiferencia;
function diffLabel(n: number) {
  if (nivelDiferencia(n) === 'cuadra') return '$0.00 (cuadra)';
  const base = `${n > 0 ? '+' : ''}${money(n)} · ${n < 0 ? 'falta' : 'sobra'}`;
  return nivelDiferencia(n) === 'menor' ? `${base} (≤ $${TOLERANCIA_MENOR})` : base;
}

/**
 * Modal de apertura / arqueo de caja.
 *
 * 🚨 REDISEÑO 18-sep-2026 (audio de Carlos, sesión con Karla). Antes esto OBLIGABA a contar 11
 * denominaciones de pesos + 6 de dólares + tipo de cambio, tanto para abrir como para cerrar, y
 * arrancaba en rojo («falta $1,418») hasta terminar el conteo. En Kydemy abrir es escribir UN
 * importe. Medido: 552 cajas de Karla, todas en Kydemy, cero en la nuestra.
 *
 * Ahora: el importe se teclea arriba y ya. El conteo por billetes SIGUE EXISTIENDO —es lo único
 * del sistema que a Karla le gustó, porque sustituye el Excel de billetes que lleva en casa— pero
 * va plegado y es opcional; si lo usa, el total se autocompleta y queda guardado en el corte.
 *
 * 🔒 29-sep-2026 (Carlos): al CERRAR el conteo es OBLIGATORIO (`conteoObligatorio`). Sin él, el
 * turno siguiente y el historial solo tenían «una cifra vacía» — y Kydemy no guarda billetes, así
 * que el único sitio donde puede existir ese dato es aquí. 🔒 30-sep-2026 (Karla): ABRIR también exige
 * el conteo (`accion="abrir"`), sin motivo si no cuadra. Única salida sin contar: cajón en $0.
 * El backend (POST /cashbox/close) lo exige igual.
 */
export default function DenominationModal({ title, cta, expected, expectedLabel, expectedHint, sugerido, sugeridoBreakdown, conteoObligatorio = false, accion = 'cerrar', aviso, usdEsperado, borradorKey, onClose, onSubmit }: {
  /** Clave del borrador del conteo (caja + acción). Sin ella no se guarda borrador. */
  borradorKey?: string;
  title: string; cta: string; expected?: number;
  /** DÓLARES (1-oct-2026): cuántos USD debe haber (null/undefined = la caja no maneja dólares todavía). El renglón
   *  «Dólares (total)» sale siempre; los dólares se cuadran en dólares, aparte de los pesos. */
  usdEsperado?: number | null;
  /** Bloque extra bajo el esperado (apertura: cobros registrados con la caja cerrada, 1-oct-2026). */
  aviso?: ReactNode;
  /** Texto del renglón del esperado. En la APERTURA es «El turno anterior dejó». */
  expectedLabel?: string;
  /** Nota chica debajo (p. ej. de qué turno y a qué hora fue ese cierre). */
  expectedHint?: string;
  /** Importe de un clic: lo que dejó el turno anterior (apertura) o el esperado (cierre). */
  sugerido?: number | null;
  /** Billetes del cierre anterior (JSON). Si abre con «Usar $X» sin contar, el cajón tiene
   *  exactamente esos billetes: se guardan como desglose HEREDADO para que «Ver billetes» no
   *  salga vacío el día que nadie contó al abrir (19-sep-2026). */
  sugeridoBreakdown?: string | null;
  /** Cierre: hay que contar billetes sí o sí (el importe sale del conteo). */
  conteoObligatorio?: boolean;
  /** 30-sep-2026 (Karla): también al ABRIR se cuenta. Al abrir no se pide motivo si no cuadra con el
   *  turno anterior (la diferencia se anota y avisa, Carlos 29-jul) ni hay nota de cierre. */
  accion?: 'abrir' | 'cerrar';
  onClose: () => void; onSubmit: (totalMXN: number, breakdown: string, motivo?: string) => Promise<void>;
}) {
  const [borrador] = useState(() => leerBorrador(borradorKey));
  const [importe, setImporte] = useState('');
  const [mxn, setMxn] = useState<Record<string, string>>(borrador?.mxn || {});
  const [usd, setUsd] = useState(borrador?.usd || '');   // dólares contados: UN total, no por billete (Mabel, 1-oct-2026)
  const [contando, setContando] = useState(conteoObligatorio || !!borrador);
  const [vacio, setVacio] = useState(!!borrador?.vacio);   // cierre con el cajón en $0: no hay nada que contar
  const [motivo, setMotivo] = useState(borrador?.motivo || '');
  const [heredado, setHeredado] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const conteoRef = useRef<HTMLDivElement>(null);
  // Al cerrar, el cursor arranca en el primer billete (el importe no se teclea). UNA sola vez.
  useEffect(() => {
    if (conteoObligatorio) conteoRef.current?.querySelector('input')?.focus();
    else ref.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cada cambio se guarda como borrador; si ya no hay nada tecleado, se quita.
  useEffect(() => {
    if (!borradorKey) return;
    const vacioTodo = !Object.values(mxn).some((v) => v && v !== '0') && !usd.trim() && !vacio && !motivo.trim();
    try {
      if (vacioTodo) localStorage.removeItem(borradorKey);
      else localStorage.setItem(borradorKey, JSON.stringify({ mxn, usd, vacio, motivo, t: Date.now() } satisfies Borrador));
    } catch { /* navegador sin almacenamiento: el modal funciona igual, sin borrador */ }
  }, [borradorKey, mxn, usd, vacio, motivo]);

  const set = (k: string, v: string) => setMxn((p) => ({ ...p, [k]: v.replace(/[^\d]/g, '') }));

  const contado = useMemo(() => DENOMINACIONES.reduce((s, d) => s + d.valor * (parseInt(mxn[d.k] || '0', 10) || 0), 0), [mxn]);
  const hayConteo = contado > 0;

  // El conteo MANDA sobre lo tecleado mientras el cajón está abierto en pantalla: si se molestó
  // en contar billetes, ese es el número bueno.
  const total = hayConteo ? contado : conteoObligatorio ? 0 : (parseFloat(importe) || 0);
  const tecleado = conteoObligatorio ? (hayConteo || vacio) : (importe.trim() !== '' || hayConteo);
  const diff = expected != null && tecleado ? Math.round((total - expected) * 100) / 100 : null;
  // 🔒 29-sep-2026 (Carlos, tras cerrar por error con $2 contra $1,350 esperados): si al CERRAR no
  // cuadra, hay que explicar por qué — mínimo MOTIVO_MIN caracteres. El backend lo exige igual.
  const usdContado = usd.trim() === '' ? null : Math.round((parseFloat(usd) || 0) * 100) / 100;
  // Diferencia en dólares: solo si se esperaban dólares o se contaron (si no, la caja no maneja dólares).
  const manejaUsd = (usdEsperado != null && usdEsperado > 0) || usdContado != null;
  const diffUsd = manejaUsd && tecleado ? Math.round(((usdContado || 0) - (usdEsperado || 0)) * 100) / 100 : null;
  const pideMotivo = conteoObligatorio && accion === 'cerrar' && ((diff != null && diff !== 0) || (diffUsd != null && diffUsd !== 0));
  const motivoOk = !pideMotivo || motivo.trim().length >= MOTIVO_MIN;

  async function submit() {
    if (!tecleado || !motivoOk) return;
    setSaving(true);
    try {
      // Se guarda el desglose SOLO si de verdad contó: un breakdown de ceros ensuciaría el
      // historial y haría creer que hubo conteo.
      let breakdown = hayConteo
        ? JSON.stringify({
            mxn: Object.fromEntries(DENOMINACIONES.map((d) => [d.k, parseInt(mxn[d.k] || '0', 10) || 0]).filter(([, n]) => n)),
            mxn_total: contado, grand: contado,
            ...(usdContado != null ? { usd: usdContado } : {}),
          })
        : (usdContado != null ? JSON.stringify({ mxn: {}, mxn_total: 0, grand: 0, usd: usdContado }) : '');
      // Un clic en «Usar $X» sin tocar el importe = mismo dinero que dejó el cierre anterior →
      // mismos billetes. Se marca `heredado` para que la pantalla diga de dónde salió y nadie lo
      // confunda con un conteo hecho hoy. Si el importe se editó después, NO se hereda.
      if (!hayConteo && heredado && sugeridoBreakdown && sugerido != null
          && Math.round(total * 100) === Math.round(sugerido * 100)) {
        try {
          const prev = JSON.parse(sugeridoBreakdown);
          if (prev?.mxn && Math.round((Number(prev.mxn_total) || 0) * 100) === Math.round(total * 100)) {
            breakdown = JSON.stringify({ mxn: prev.mxn, mxn_total: prev.mxn_total, grand: prev.mxn_total, heredado: true });
          }
        } catch { /* desglose anterior ilegible: se abre sin desglose */ }
      }
      await onSubmit(Math.round(total * 100) / 100, breakdown, conteoObligatorio && accion === 'cerrar' && motivo.trim() ? motivo.trim() : undefined);
    } finally { setSaving(false); }
  }

  const row = (d: Denominacion) => {
    const n = parseInt(mxn[d.k] || '0', 10) || 0;
    return (
      <div key={d.k} className="flex items-center justify-between gap-2 py-0.5 text-sm">
        <span className="w-28 text-content-2">{d.etiqueta}</span>
        <span className="text-content-3">×</span>
        <input inputMode="numeric" data-denom={d.k} value={mxn[d.k] || ''} onChange={(e) => set(d.k, e.target.value)} placeholder="0" className={inputCls} />
        <span className="w-24 text-right tabular-nums text-content">{n ? money(d.valor * n) : '—'}</span>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      {/* Sin onClick: un clic fuera ya NO cierra (Mabel 5-oct: se le cerraba al moverse). Solo ✕ o Cancelar. */}
      <div className="modal-overlay absolute inset-0 bg-black/50" />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">{title}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>

        {expected != null && (
          <div className="mb-3 rounded-lg bg-surface-2 px-3 py-2 text-sm text-content-2">
            {expectedLabel || 'Efectivo esperado en caja'}: <b className="text-content">{money(expected)}</b>
            {expectedHint && <div className="mt-0.5 text-xs text-content-3">{expectedHint}</div>}
          </div>
        )}
        {aviso}
        {borrador && (
          <div data-testid="borrador-conteo" className="mb-3 rounded-lg border border-brand-blue/40 bg-brand-blue/10 px-3 py-2 text-sm text-content-2">
            <b className="text-content">Recuperamos tu conteo sin guardar.</b> Revísalo: el dinero se registra hasta que le des «{cta}».
          </div>
        )}

        {/* IMPORTE — lo primero y lo único obligatorio */}
        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">Importe</label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-content-3">$</span>
          <input
            ref={conteoObligatorio ? undefined : ref} inputMode="decimal" value={hayConteo ? String(contado) : conteoObligatorio ? (vacio ? '0' : '') : importe}
            onChange={(e) => { setImporte(e.target.value.replace(/[^\d.]/g, '')); setHeredado(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && tecleado && motivoOk && !saving) submit(); }}
            disabled={hayConteo || conteoObligatorio} placeholder={conteoObligatorio ? 'Sale del conteo de abajo' : '0.00'}
            className="w-full rounded-xl border border-line bg-surface py-3 pl-8 pr-3 font-display text-2xl font-bold text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 disabled:text-content-2"
          />
        </div>
        {hayConteo && !conteoObligatorio && (
          <p className="mt-1 text-xs text-content-3">Lo está tomando del conteo de billetes. Borra el conteo para escribirlo a mano.</p>
        )}
        {conteoObligatorio && (
          <p className="mt-1 text-xs text-content-3" data-testid="conteo-obligatorio">
            {accion === 'abrir'
              ? <>Para abrir hay que <b>contar billetes y monedas</b>: así queda claro con cuánto arranca este turno.</>
              : <>Para cerrar hay que <b>contar billetes y monedas</b>: así el siguiente turno y el historial saben con qué se quedó la caja.</>}
          </p>
        )}

        {/* Un clic para el caso normal: la caja abre con lo que dejó el turno anterior. */}
        {!conteoObligatorio && sugerido != null && sugerido > 0 && !hayConteo && Math.round(total * 100) !== Math.round(sugerido * 100) && (
          <button onClick={() => { setImporte(String(sugerido)); setHeredado(true); }}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface">
            <CornerDownLeft size={14} /> Usar {money(sugerido)}
            <span className="text-xs font-normal text-content-3">{expectedLabel === 'El turno anterior dejó' ? '(lo del turno anterior)' : '(lo esperado)'}</span>
          </button>
        )}

        {/* CONTEO DE BILLETES — opcional y plegado. Sustituye el Excel que Karla lleva en casa. */}
        <button onClick={() => { if (!conteoObligatorio) setContando((v) => !v); }}
          className="mt-4 flex w-full items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2 text-sm font-medium text-content-2 transition hover:bg-surface-2">
          {contando ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          <Banknote size={15} /> Contar billetes
          <span className="ml-auto text-xs font-normal text-content-3">
            {hayConteo ? money(contado) : conteoObligatorio ? 'obligatorio' : 'opcional'}
          </span>
        </button>
        {contando && (
          <div className="mt-2 rounded-xl border border-line bg-surface-2/50 p-3">
            <div ref={conteoRef}>{DENOMINACIONES.map(row)}</div>
            <div className="mt-1 flex justify-between border-t border-line pt-1 text-sm font-semibold">
              <span className="text-content-2">Total contado</span>
              <span className="tabular-nums text-content">{money(contado)}</span>
            </div>
            {/* Dólares: un solo total (en su Excel ponían «20 dólares» y ya). Se cuadran aparte de los pesos. */}
            <div className="mt-2 flex items-center justify-between gap-2 border-t border-line pt-2 text-sm">
              <span className="w-28 text-content-2">Dólares (total)</span>
              <span className="text-content-3">USD</span>
              <input inputMode="decimal" data-testid="usd-conteo" value={usd} onChange={(e) => setUsd(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0" className={inputCls} />
              <span className="w-24 text-right text-xs text-content-3">{usdEsperado != null && usdEsperado > 0 ? `debe haber ${usdEsperado}` : ''}</span>
            </div>
            {conteoObligatorio && !hayConteo && (
              <label className="mt-2 flex items-center gap-2 text-xs text-content-2">
                <input type="checkbox" checked={vacio} onChange={(e) => setVacio(e.target.checked)} data-testid="cajon-vacio" />
                {accion === 'abrir' ? 'El cajón está vacío ($0), no hay nada que contar' : 'El cajón se queda vacío ($0), no hay nada que contar'}
              </label>
            )}
            {hayConteo && (
              <button onClick={() => setMxn({})} className="mt-2 text-xs text-content-3 underline transition hover:text-content-2">
                Borrar conteo
              </button>
            )}
          </div>
        )}

        {/* Diferencia: solo cuando ya hay un importe. Antes salía en rojo desde el primer
            segundo, con la caja aún sin contar, y parecía que algo estaba mal. */}
        {diff != null && (
          <p className={`mt-3 text-center text-sm font-medium ${diffColor(diff)}`}>Diferencia: {diffLabel(diff)}</p>
        )}
        {diffUsd != null && (
          <p data-testid="diferencia-usd" className={`mt-1 text-center text-sm font-medium ${diffColor(diffUsd)}`}>
            Dólares: {diffUsd === 0 ? 'cuadran' : `${diffUsd > 0 ? '+' : ''}${diffUsd} USD · ${diffUsd < 0 ? 'faltan' : 'sobran'}`}
            {usdEsperado != null && usdEsperado > 0 ? ` (debe haber ${usdEsperado} USD)` : ''}
          </p>
        )}
        {/* 10-oct-2026 (arqueo de Dayan): faltan pesos y sobran dólares = pagaron con dólares algo que se cobró
            en pesos. No se cuadra «inventando» monedas: se registra el cambio en la caja. */}
        {accion === 'cerrar' && diff != null && diff < 0 && diffUsd != null && diffUsd > 0 && (
          <p data-testid="pista-entraron-dolares" className="mx-auto mt-2 max-w-sm rounded-lg bg-amber-500/10 px-3 py-2 text-center text-xs text-amber-800 dark:text-amber-300">
            ¿Pagaron con dólares algo que se registró en pesos? Cancela, usa el botón <b>«Entraron dólares»</b> de la caja con {diffUsd} USD y vuelve a cerrar: así cuadra.
          </p>
        )}
        {/* Al cerrar el cuadro está siempre: si cuadra es una NOTA opcional; si no cuadra es el
            MOTIVO y es obligatorio (Carlos 29-sep). Se guarda igual en `notes`. */}
        {conteoObligatorio && accion === 'cerrar' && (
          <div className="mt-3" data-testid={pideMotivo ? 'motivo-diferencia' : 'nota-cierre'}>
            <label htmlFor="motivo-cierre" className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-3">
              {pideMotivo
                ? <>¿Por qué no cuadra? <span className="normal-case">(obligatorio)</span></>
                : <>Nota <span className="normal-case">(opcional)</span></>}
            </label>
            <textarea id="motivo-cierre" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 255))}
              placeholder={pideMotivo ? 'Ej.: se dio cambio de más a una alumna; faltó registrar un retiro…' : 'Algo que quieras dejar anotado en este cierre'}
              className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30" />
            <p className={`mt-0.5 text-right text-[11px] ${motivoOk ? 'text-content-3' : 'text-amber-600 dark:text-amber-400'}`}>
              {!pideMotivo ? 'Queda guardada en el cierre.' : motivoOk ? 'Queda guardado en el cierre.' : `Faltan ${MOTIVO_MIN - motivo.trim().length} caracteres (mínimo ${MOTIVO_MIN})`}
            </p>
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
          <button disabled={saving || !tecleado || !motivoOk} onClick={submit}
            className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
            {saving ? 'Guardando…' : cta}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
